import { createFileRoute, Link } from "@tanstack/react-router";
import { HERO, LANGS } from "@/lib/villa-content";
import { useEffect, useState } from "react";
import outdoorKitchenNight from "@/assets/villa/20200719-zicht-op-buitenkeuken-avond-4_orig.jpg.asset.json";
import gardenFull from "@/assets/villa/20200722-tuin-helemaal_orig.jpg.asset.json";
import gardenLoungersPalm from "@/assets/villa/20200722-ligzetels-onder-palm_orig.jpg.asset.json";
import hoofdfoto from "@/assets/villa/hoofdfoto_orig.jpg.asset.json";
import archDay from "@/assets/villa/20240419-184622_orig.jpg.asset.json";
import outdoorDining from "@/assets/villa/20240614-134837_orig.jpg.asset.json";
import poolDusk from "@/assets/villa/20240614-215924-2_orig.jpg.asset.json";
import outdoorKitchenGreen from "@/assets/villa/20210729-084915-24494_orig.jpg.asset.json";
import gardenBougainvillea from "@/assets/villa/img-20181111-wa0014_orig.jpg.asset.json";

const SLIDES = [
  { src: HERO, alt: "Casa Mi Pajobe villa with private pool, La Nucia, Costa Blanca" },
  { src: poolDusk.url, alt: "Private pool at dusk" },
  { src: hoofdfoto.url, alt: "Villa exterior with pool in daylight" },
  { src: archDay.url, alt: "Arched pergola by the pool in daylight" },
  { src: outdoorKitchenNight.url, alt: "Outdoor kitchen lit up in the evening" },
  { src: outdoorKitchenGreen.url, alt: "Outdoor kitchen with green tiled backsplash" },
  { src: outdoorDining.url, alt: "Outdoor dining area under the pergola" },
  { src: gardenFull.url, alt: "Mediterranean garden view" },
  { src: gardenLoungersPalm.url, alt: "Loungers under palm trees by the pool" },
  { src: gardenBougainvillea.url, alt: "Garden with bougainvillea in bloom" },
];

const PHRASES = [
  "Private pool · sleeps 8",
  "Outdoor kitchen · 1000m² garden",
  "La Nucia · 5 km from Benidorm",
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Casa Mi Pajobe, Choose your language" },
      { name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1" },
      {
        name: "description",
        content:
          "Casa Mi Pajobe, private villa with pool and outdoor kitchen in La Nucia, Costa Blanca. 4 bedrooms, sleeps 8. Book direct in EN, NL, FR, DE.",
      },
      { property: "og:title", content: "Casa Mi Pajobe, Villa Costa Blanca" },
      {
        property: "og:description",
        content:
          "Private villa with pool on the Costa Blanca, outdoor kitchen, 4 bedrooms, sleeps 8. La Nucia, near Benidorm.",
      },
      { property: "og:url", content: "https://www.casamipajobe.com/" },
      { property: "og:type", content: "website" },
      {
        property: "og:image",
        content: "https://www.casamipajobe.com/__l5e/assets-v1/1c7d9044-e1c0-44c1-a661-425b270f95a5/og-preview.jpeg",
      },
      {
        name: "twitter:image",
        content: "https://www.casamipajobe.com/__l5e/assets-v1/1c7d9044-e1c0-44c1-a661-425b270f95a5/og-preview.jpeg",
      },
    ],
    links: [
      {
        rel: "preload",
        as: "image",
        href: HERO,
        fetchPriority: "high",
      },
      { rel: "canonical", href: "https://www.casamipajobe.com/" },
      { rel: "alternate", hrefLang: "nl", href: "https://www.casamipajobe.com/nl" },
      { rel: "alternate", hrefLang: "en", href: "https://www.casamipajobe.com/en" },
      { rel: "alternate", hrefLang: "fr", href: "https://www.casamipajobe.com/fr" },
      { rel: "alternate", hrefLang: "de", href: "https://www.casamipajobe.com/de" },
      { rel: "alternate", hrefLang: "x-default", href: "https://www.casamipajobe.com/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebSite",
              "@id": "https://www.casamipajobe.com/#website",
              name: "Casa Mi Pajobe",
              url: "https://www.casamipajobe.com/",
              inLanguage: ["en", "nl", "fr", "de"],
            },
            {
              "@type": "LodgingBusiness",
              "@id": "https://www.casamipajobe.com/#lodging",
              name: "Casa Mi Pajobe",
              description:
                "Detached holiday villa with private pool and large outdoor kitchen in La Nucia, Costa Blanca. 4 bedrooms, sleeps up to 8.",
              url: "https://www.casamipajobe.com/",
              image: `https://www.casamipajobe.com${HERO}`,
              priceRange: "€€",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Urbanización Panorama",
                addressLocality: "La Nucia",
                addressRegion: "Alicante",
                postalCode: "03530",
                addressCountry: "ES",
              },
              amenityFeature: [
                { "@type": "LocationFeatureSpecification", name: "Private pool", value: true },
                { "@type": "LocationFeatureSpecification", name: "Outdoor kitchen", value: true },
                { "@type": "LocationFeatureSpecification", name: "Air conditioning", value: true },
                { "@type": "LocationFeatureSpecification", name: "Wi-Fi", value: true },
              ],
              numberOfRooms: 4,
              identifier: "VT-454687-A",
            },
          ],
        }),
      },
    ],
  }),
  component: LanguageSplash,
});

function LanguageSplash() {
  const [slide, setSlide] = useState(0);
  const [phrase, setPhrase] = useState(0);
  useEffect(() => {
    const a = setInterval(() => setSlide((n) => (n + 1) % SLIDES.length), 5000);
    const b = setInterval(() => setPhrase((n) => (n + 1) % PHRASES.length), 3500);
    return () => {
      clearInterval(a);
      clearInterval(b);
    };
  }, []);
  return (
    <div className="relative isolate h-[100svh] min-h-[640px] w-full overflow-hidden">
      {SLIDES.map((s, idx) => (
        <img
          key={s.src}
          src={s.src}
          alt={s.alt}
          className={`absolute inset-0 -z-10 h-full w-full object-cover transition-opacity duration-1000 ${idx === slide ? "opacity-100" : "opacity-0"}`}
          width={1600}
          height={1067}
          fetchPriority={idx === 0 ? "high" : "low"}
          decoding="async"
          loading={idx === 0 ? "eager" : "lazy"}
        />
      ))}
      <div className="absolute inset-0 -z-10 bg-black/55" />
      <div className="mx-auto flex h-full max-w-3xl flex-col items-center justify-center gap-3 px-4 py-6 text-center text-white sm:px-6">
        <p className="whitespace-pre-line text-xs font-medium uppercase tracking-[0.3em] text-white/80 text-shadow-soft sm:text-sm">
          LA NUCIA · COSTA BLANCA · {"\n"}
          VT-454687-A
        </p>
        <h1 className="text-shadow-soft font-display text-3xl font-semibold sm:text-5xl md:text-6xl">
          Casa Mi Pajobe, Private Villa with Pool on the Costa Blanca
        </h1>
        <p
          key={phrase}
          className="text-shadow-soft animate-fade-in text-xs font-medium uppercase tracking-[0.25em] text-white sm:text-sm"
        >
          {PHRASES[phrase]}
        </p>
        <p className="text-shadow-soft max-w-xl text-sm text-white/90 sm:text-lg">
          Welcome · Welkom · Bienvenue · Willkommen
        </p>
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/90 sm:text-sm">
          Choose your language
        </p>

        <div className="mt-2 grid w-full gap-2 sm:grid-cols-2 sm:gap-3">
          {LANGS.filter((l) => l.code !== "fr").map((l) => (
            <Link
              key={l.code}
              to="/$lang"
              params={{ lang: l.code }}
              className="group grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-2xl border border-white/30 bg-white/10 px-3 py-3 text-left text-white backdrop-blur-md transition hover:border-white hover:bg-white hover:text-primary sm:gap-4 sm:px-5 sm:py-4"
            >
              <img
                src={`https://flagcdn.com/w80/${l.flagCode}.png`}
                srcSet={`https://flagcdn.com/w160/${l.flagCode}.png 2x`}
                alt=""
                aria-hidden
                width={36}
                height={27}
                loading="lazy"
                decoding="async"
                className="h-6 w-8 shrink-0 rounded-sm object-cover shadow-sm ring-1 ring-black/10 sm:h-7 sm:w-9"
              />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold sm:text-lg">{l.label}</span>
                <span className="block truncate text-[11px] text-white/90 group-hover:text-primary sm:text-xs">
                  {l.tagline}
                </span>
              </span>
              <span aria-hidden className="shrink-0 text-lg transition group-hover:translate-x-1 sm:text-xl">→</span>
            </Link>
          ))}
          <a
            href="https://www.casamipajobe.com/book"
            className="group grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-2xl border border-white/30 bg-white/10 px-3 py-3 text-left text-white backdrop-blur-md transition hover:border-white hover:bg-white hover:text-primary sm:gap-4 sm:px-5 sm:py-4"
          >
            <span
              aria-hidden
              className="grid h-6 w-8 shrink-0 place-items-center rounded-sm bg-primary text-base text-primary-foreground sm:h-7 sm:w-9"
            >
              ✓
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold sm:text-lg">Book now</span>
              <span className="block truncate text-[11px] text-white/90 group-hover:text-primary sm:text-xs">
                Check availability &amp; reserve
              </span>
            </span>
            <span aria-hidden className="shrink-0 text-lg transition group-hover:translate-x-1 sm:text-xl">→</span>
          </a>
        </div>

        <p className="mt-3 max-w-2xl text-[11px] leading-relaxed text-white/85 sm:text-sm">
          Casa Mi Pajobe is a detached holiday villa with a private pool, a large outdoor kitchen and
          an enclosed Mediterranean garden in La Nucia, 5 km from Benidorm on the Costa Blanca. Four
          bedrooms, two bathrooms, air conditioning, Wi-Fi and private parking, sleeping up to eight
          guests. Beaches, Benidorm's theme parks, Altea, Guadalest and Alicante airport are all
          within easy reach. Book directly with the owners in Dutch, English, French or German.
        </p>
        <p className="mt-2 text-[10px] text-white/85 sm:text-xs">
          Registro Generalitat Valenciana · VT-454687-A
        </p>
      </div>
      <div className="pointer-events-auto absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2">
        {SLIDES.map((_, idx) => (
          <button
            key={idx}
            type="button"
            aria-label={`Show photo ${idx + 1}`}
            onClick={() => setSlide(idx)}
            className={`h-1.5 rounded-full transition-all ${idx === slide ? "w-8 bg-white" : "w-4 bg-white/50 hover:bg-white/80"}`}
          />
        ))}
      </div>
    </div>
  );
}